import { lazy, Suspense } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useAppSelector } from '../../app/hooks.js';
import { selectDrawerOpen } from '../../store/selectors/index.js';
import AppHeader from './AppHeader.js';
import SidePanel from './SidePanel.js';
import AttributionBar from './AttributionBar.js';
import MetadataDialog from '../metadata/MetadataDialog.js';
import IdentifyPanel from '../identify/IdentifyPanel.js';
import MeasurePanel from '../measure/MeasurePanel.js';
import MeasureTools from '../measure/MeasureTools.js';

/**
 * The map is code-split away from everything else. The catalog is usable while
 * the ArcGIS SDK is still downloading, which is the difference between a shell
 * that appears in a few hundred milliseconds and one that waits on several
 * megabytes of mapping library.
 */
const MapHost = lazy(() => import('../../map/MapHost.js'));

const DRAWER_WIDTH = 384;

export default function Shell() {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const drawerOpen = useAppSelector(selectDrawerOpen);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AppHeader />

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
        <SidePanel width={DRAWER_WIDTH} compact={isCompact} />

        <Box
          component="main"
          id="main-content"
          sx={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            // On compact viewports the panel overlays the map instead of
            // squeezing it; a map narrower than ~320px is not usable.
            ...(isCompact || !drawerOpen ? {} : { marginLeft: 0 }),
          }}
        >
          <Suspense fallback={<MapLoading />}>
            <MapHost />
          </Suspense>
          <IdentifyPanel />
          <MeasureTools />
          <MeasurePanel />
          <AttributionBar />
        </Box>
      </Box>

      <MetadataDialog />
    </Box>
  );
}

function MapLoading() {
  return (
    <Box
      sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}
      role="status"
      aria-live="polite"
      aria-label="Loading map"
    >
      <Box sx={{ width: 220 }}>
        {/* The name goes on the progressbar element itself; a label on an
            ancestor does not name it (WCAG 4.1.2, aria-progressbar-name). */}
        <LinearProgress aria-label="Loading map" />
      </Box>
    </Box>
  );
}
